/**
 * 结构类 op：编组 / 解组 / 层序 / 删除 / 原地复制。
 *
 * 三条关键约定：
 *  1) **零副作用**：构造阶段只做计算与快照，DOM 只在 `execute` 时变
 *     （靠闭包型 custom patch 实现，见 patch.ts）
 *  2) **保持元素身份**：apply 走真实 DOM 操作（appendChild/insertBefore），不重建子树
 *     —— 选中集与列表持有的引用不会失效
 *  3) **最小 diff**：不需要补偿时不写 transform（同父级编组、解组无 transform 的 <g>）；
 *     跨坐标系必须补偿：新局部 = inv(M新父) · M旧父 · 旧局部
 */
import { SVG_NS, pathOf } from '../dom';
import { invert, isIdentityMat, mul, toString, translate, type Mat } from '../geom/matrix';
import { nextTransformAttribute } from '../geom/handles';
import { modelMatrixToRoot } from '../geom/model-matrix';
import { parseTransformList } from '../geom/transform';
import type { Command } from '../history';
import { rewriteIdsInClone } from '../ids';
import { makeStructuralPatch, snapshotOf, type Patch } from '../patch';
import type { TransformDeps } from './transform';

export type OrderMode = 'front' | 'back' | 'forward' | 'backward';

export interface StructureResult {
  cmd: Command;
  /** 操作后建议的选中集（避免引用已消失的节点） */
  selection: Element[];
}

export interface DuplicateResult extends StructureResult {
  copies: Element[];
}

const setTransform = (el: Element, m: Mat | null): void => {
  if (m === null) el.removeAttribute('transform');
  else el.setAttribute('transform', toString(m));
};

const cmpPath = (a: readonly number[], b: readonly number[]): number => {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const d = (a[i] as number) - (b[i] as number);
    if (d !== 0) return d;
  }
  return a.length - b.length;
};

/** 按文档顺序排序（编组的插入位置依赖它） */
export function inDocOrder(els: readonly Element[]): Element[] {
  const root = els[0]?.ownerDocument?.documentElement ?? null;
  if (!root) return [...els];
  return [...els].sort((a, b) => cmpPath(pathOf(a, root) ?? [], pathOf(b, root) ?? []));
}

/** 最近公共父级（新 <g> 挂在它下面） */
export function commonParent(els: readonly Element[]): Element | null {
  let candidate: Element | null = els[0]?.parentElement ?? null;
  for (const el of els) {
    while (candidate && !candidate.contains(el)) candidate = candidate.parentElement;
    if (!candidate) return null;
  }
  return candidate;
}

const groupByParent = (els: readonly Element[]): Map<Element, Element[]> => {
  const map = new Map<Element, Element[]>();
  for (const el of inDocOrder(els)) {
    const parent = el.parentElement;
    if (!parent) continue;
    const list = map.get(parent);
    if (list) list.push(el);
    else map.set(parent, [el]);
  }
  return map;
};

export function groupElements(els: readonly Element[], root: Element): StructureResult {
  const list = inDocOrder(els.filter((e) => e.parentElement !== null));
  if (list.length < 2) return { cmd: { label: '编组', patches: [] }, selection: list };

  const parent = commonParent(list);
  if (!parent) return { cmd: { label: '编组', patches: [] }, selection: list };

  const g = root.ownerDocument.createElementNS(SVG_NS, 'g') as SVGGElement;
  const anchor = list[list.length - 1] as Element;
  const ref = anchor.nextSibling; // 位置先记下（元素随后会移动）
  const before = snapshotOf(parent);
  const invNewParent = invert(modelMatrixToRoot(parent, root));

  // 只计算，不改 DOM：只有跨父级者需要补偿 transform
  const plans = list.map((el) => {
    const oldParent = el.parentElement as Element;
    const prevValue = el.getAttribute('transform');
    if (oldParent === parent) return { el, prevValue, nextMat: undefined as Mat | null | undefined };
    const local = invNewParent
      ? mul(mul(invNewParent, modelMatrixToRoot(oldParent, root)), parseTransformList(prevValue).mat)
      : parseTransformList(prevValue).mat;
    return { el, prevValue, nextMat: isIdentityMat(local) ? (null as Mat | null) : local };
  });

  return {
    cmd: {
      label: '编组',
      patches: [
        makeStructuralPatch(parent, before, '编组', () => {
          for (const plan of plans) {
            if (plan.nextMat !== undefined) setTransform(plan.el, plan.nextMat);
            g.appendChild(plan.el);
          }
          parent.insertBefore(g, ref);
        }),
      ],
    },
    selection: [g],
  };
}

export function ungroupElements(els: readonly Element[], _root: Element): StructureResult {
  const groups = inDocOrder(els.filter((e) => e.tagName.toLowerCase() === 'g' && e.parentElement));
  if (groups.length === 0) return { cmd: { label: '解组', patches: [] }, selection: [...els] };

  const patches: Patch[] = [];
  const freed: Element[] = [];

  for (const g of groups) {
    const parent = g.parentElement as Element;
    const before = snapshotOf(parent);
    const gMat = parseTransformList(g.getAttribute('transform')).mat;
    const children = Array.from(g.children);
    const needsOffset = !isIdentityMat(gMat);

    // <g> 的矩阵**前置乘**到子元素上（无 transform 的组不写属性，保持最小 diff）
    const plans = children.map((child) => {
      if (!needsOffset) return { child, nextMat: undefined as Mat | null | undefined };
      const local = mul(gMat, parseTransformList(child.getAttribute('transform')).mat);
      return { child, nextMat: isIdentityMat(local) ? (null as Mat | null) : local };
    });
    freed.push(...children);

    const ref = g.nextSibling;
    patches.push(
      makeStructuralPatch(parent, before, '解组', () => {
        for (const plan of plans) if (plan.nextMat !== undefined) setTransform(plan.child, plan.nextMat);
        for (const child of children) parent.insertBefore(child, ref);
        if (g.parentElement === parent) parent.removeChild(g);
      }),
    );
  }
  return { cmd: { label: '解组', patches }, selection: freed };
}

export function reorderElements(els: readonly Element[], _root: Element, mode: OrderMode): Command {
  const label = { front: '置顶', back: '置底', forward: '上移一层', backward: '下移一层' }[mode];
  const patches: Patch[] = [];

  for (const [parent, list] of groupByParent(els)) {
    const current = Array.from(parent.children);
    const selected = new Set<Element>(list);
    const index = (el: Element): number => current.indexOf(el);
    const ordered = [...list].sort((a, b) => index(a) - index(b));
    const others = current.filter((c) => !selected.has(c));

    let plan: Element[];
    switch (mode) {
      case 'front':
        plan = [...others, ...ordered];
        break;
      case 'back':
        plan = [...ordered, ...others];
        break;
      case 'forward': {
        plan = [...current];
        for (let i = plan.length - 2; i >= 0; i--) {
          const el = plan[i] as Element;
          const next = plan[i + 1] as Element;
          if (selected.has(el) && !selected.has(next)) {
            plan[i] = next;
            plan[i + 1] = el;
          }
        }
        break;
      }
      default: {
        plan = [...current];
        for (let i = 1; i < plan.length; i++) {
          const el = plan[i] as Element;
          const prev = plan[i - 1] as Element;
          if (selected.has(el) && !selected.has(prev)) {
            plan[i] = prev;
            plan[i - 1] = el;
          }
        }
        break;
      }
    }

    // 已在目标顺序 → 空操作（不产生 patch，也就不会进历史）
    if (plan.every((el, i) => el === current[i])) continue;

    const before = snapshotOf(parent);
    patches.push(
      makeStructuralPatch(parent, before, label, () => {
        for (const el of plan) parent.appendChild(el); // 按目标顺序重挂
      }),
    );
  }
  return { label, patches };
}

export function deleteElements(els: readonly Element[], root: Element): StructureResult {
  const patches: Patch[] = [];
  for (const [parent, list] of groupByParent(els.filter((e) => e !== root))) {
    const before = snapshotOf(parent);
    patches.push(
      makeStructuralPatch(parent, before, '删除', () => {
        for (const el of list) if (el.parentElement === parent) parent.removeChild(el);
      }),
    );
  }
  return { cmd: { label: '删除', patches }, selection: [] };
}

/** 原地复制：id 一律重写为 ext-N（避免重复 id），并偏移一小段位移 */
export function duplicateElements(
  deps: TransformDeps,
  els: readonly Element[],
  dx = 12,
  dy = 12,
): DuplicateResult {
  const patches: Patch[] = [];
  const copies: Element[] = [];

  for (const [parent, list] of groupByParent(els.filter((e) => e !== deps.root))) {
    const before = snapshotOf(parent);
    // 克隆体在构造阶段就备好（**尚未入树**，所以 ext-id 不会自撞），apply 时只做插入
    const plans = list.map((el) => {
      const clone = el.cloneNode(true) as Element;
      rewriteIdsInClone(clone, deps.root);
      clone.setAttribute(
        'transform',
        nextTransformAttribute(clone.getAttribute('transform'), translate(dx, dy), deps.parentMatOf(clone)),
      );
      copies.push(clone);
      return { el, clone };
    });

    patches.push(
      makeStructuralPatch(parent, before, '复制', () => {
        for (const plan of plans) parent.insertBefore(plan.clone, plan.el.nextSibling);
      }),
    );
  }
  return { cmd: { label: '复制', patches }, selection: copies, copies };
}
